'use client';

import { useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Link, Typography } from '@mui/material';
import { FormSelect, FormTextField } from '@/components/common/FormComponents';
import type { UpdateProfileRequest } from '@/api/ApiClient';
import { US_JURISDICTION_OPTIONS } from '@/lib/constants/usJurisdictionOptions';
import { useBackend } from '@/hooks/useBackend';
import { useSnackbar } from '@/hooks/useSnackbar';
import { useSystemConfig } from '@/hooks/useSystemConfig';
import { useAuthStore } from '@/stores/authStore';
import { log } from '@/lib/utils/logger';
import { inputDateToApiDate, todayInputDate } from '@/lib/users/collaboratorTimesliceForm';

import { isInhouseUser } from '@/lib/auth/isInhouseUser';

const MAX_LEN = 30;

function normalizeText(value: string | undefined | null): string {
  return (value ?? '').trim();
}

function affiliationChangedFromSaved(
  current: {
    institution?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
    country?: string | null;
  },
  form: { institution: string; city: string; state: string; zip: string; country: string }
): boolean {
  return (
    normalizeText(form.institution) !== normalizeText(current.institution) ||
    normalizeText(form.city) !== normalizeText(current.city) ||
    normalizeText(form.state) !== normalizeText(current.state) ||
    normalizeText(form.zip) !== normalizeText(current.zip) ||
    normalizeText(form.country || 'USA') !== normalizeText(current.country || 'USA')
  );
}

function buildSsoNoticeText(changed: { email: boolean; affiliation: boolean }): string {
  if (changed.email && changed.affiliation) {
    return 'Your contact email and affiliation are updated in LiST only. They do not change how you sign in (SSO).';
  }
  if (changed.email) {
    return 'Your contact email is updated in LiST only. It does not change how you sign in (SSO).';
  }
  return 'Your affiliation is updated in LiST only. It does not change how you sign in (SSO).';
}

export function ProfilePreferencesTab() {
  const backend = useBackend();
  const { showSnackbar } = useSnackbar();
  const systemConfig = useSystemConfig();
  const { currentUser, fetchUserDataFromServer } = useAuthStore();
  const [saving, setSaving] = useState(false);
  const [ssoNotice, setSsoNotice] = useState<{ email: boolean; affiliation: boolean } | null>(null);

  const inhouse = currentUser ? isInhouseUser(currentUser) : true;

  const [email, setEmail] = useState(() => currentUser?.email ?? '');
  const [middleName, setMiddleName] = useState(() => currentUser?.middleName ?? '');
  const [position, setPosition] = useState(() => currentUser?.position ?? '');
  const [orcId, setOrcId] = useState(() => currentUser?.orcId ?? '');
  const [phone, setPhone] = useState(() => currentUser?.phone ?? '');
  const [institution, setInstitution] = useState(() => currentUser?.institution ?? '');
  const [city, setCity] = useState(() => currentUser?.city ?? '');
  const [stateValue, setStateValue] = useState(() => currentUser?.state ?? '');
  const [zip, setZip] = useState(() => currentUser?.zip ?? '');
  const [country, setCountry] = useState(() => currentUser?.country ?? 'USA');
  const [effectiveFromInput, setEffectiveFromInput] = useState(todayInputDate);
  const [effectiveFromError, setEffectiveFromError] = useState<string | undefined>();

  const countryTrimmed = country.trim();
  const countryEffectiveIsUsa = !countryTrimmed || countryTrimmed.toUpperCase() === 'USA';
  const useUsStateDropdown = countryEffectiveIsUsa;

  const usStateSelectOptions = useMemo(
    () =>
      US_JURISDICTION_OPTIONS.map((j) => ({
        value: j.code,
        label: `${j.label} (${j.code})`,
      })),
    []
  );

  if (!currentUser) {
    return null;
  }

  const handleSave = async () => {
    setSaving(true);
    setSsoNotice(null);
    setEffectiveFromError(undefined);
    try {
      const emailChanged = normalizeText(email).toLowerCase() !== normalizeText(currentUser.email).toLowerCase();
      const affiliationChanged =
        !inhouse &&
        affiliationChangedFromSaved(currentUser, {
          institution,
          city,
          state: stateValue,
          zip,
          country,
        });

      if (affiliationChanged && !effectiveFromInput.trim()) {
        setEffectiveFromError('Effective date is required');
        return;
      }

      const req: UpdateProfileRequest = {
        email: email.trim() || undefined,
        middleName: middleName.trim() || undefined,
        position: position.trim() || undefined,
        orcId: orcId.trim() || undefined,
        phone: phone.trim() || undefined,
        effectiveFrom: affiliationChanged ? inputDateToApiDate(effectiveFromInput) : undefined,
      };

      if (!inhouse) {
        req.institution = institution.trim() || undefined;
        req.city = city.trim() || undefined;
        req.state = stateValue.trim() || undefined;
        req.zip = zip.trim() || undefined;
        req.country = country.trim() || undefined;
      }

      await backend.user.updateProfile(req);
      await fetchUserDataFromServer();
      showSnackbar('Profile saved successfully', 'success');
      if (emailChanged || affiliationChanged) {
        setSsoNotice({ email: emailChanged, affiliation: affiliationChanged });
      }
    } catch (err: unknown) {
      log.error('[ProfilePreferencesTab] Error saving profile:', err);
      const message = err instanceof Error ? err.message : 'Failed to save profile';
      showSnackbar(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const dataManagerEmail = systemConfig?.dataManagerEmail?.trim();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
      {ssoNotice ? (
        <Alert severity="info" onClose={() => setSsoNotice(null)}>
          <Typography variant="body2" component="span">
            {buildSsoNoticeText(ssoNotice)}{' '}
            {dataManagerEmail ? (
              <>
                Email{' '}
                <Link href={`mailto:${dataManagerEmail}`} underline="hover">
                  {dataManagerEmail}
                </Link>{' '}
                to request a sign-in update.
              </>
            ) : (
              <>Contact the data manager to request a sign-in update.</>
            )}
          </Typography>
        </Alert>
      ) : null}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
          <FormTextField label="Email" value={email} onChange={(e) => setEmail(e.target.value)} fullWidth />
        </Box>
        <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
          <FormTextField label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} fullWidth />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
          <FormTextField
            label="Middle name"
            value={middleName}
            onChange={(e) => setMiddleName(e.target.value)}
            fullWidth
          />
        </Box>
        <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
          <FormTextField label="Position" value={position} onChange={(e) => setPosition(e.target.value)} fullWidth />
        </Box>
        <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
          <FormTextField label="ORCID" value={orcId} onChange={(e) => setOrcId(e.target.value)} fullWidth />
        </Box>
      </Box>

      {!inhouse ? (
        <>
          <FormTextField
            label="Institution"
            value={institution}
            onChange={(e) => setInstitution(e.target.value)}
            fullWidth
            required
          />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
              <FormTextField label="City" value={city} onChange={(e) => setCity(e.target.value)} fullWidth required />
            </Box>
            <Box sx={{ flex: '1 1 160px', minWidth: 0 }}>
              <FormTextField
                label="Zip / Postal code"
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                fullWidth
                inputProps={{ maxLength: MAX_LEN }}
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
              {useUsStateDropdown ? (
                <FormSelect
                  label="State"
                  value={stateValue}
                  onChange={(e) => setStateValue(String(e.target.value))}
                  options={usStateSelectOptions}
                />
              ) : (
                <FormTextField
                  label="State / Province"
                  value={stateValue}
                  onChange={(e) => setStateValue(e.target.value)}
                  fullWidth
                />
              )}
            </Box>
            <Box sx={{ flex: '1 1 160px', minWidth: 0 }}>
              <FormTextField
                label="Country"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                fullWidth
                inputProps={{ maxLength: MAX_LEN }}
              />
            </Box>
          </Box>
          <FormTextField
            label="Affiliation effective from"
            type="date"
            required
            value={effectiveFromInput}
            onChange={(e) => setEffectiveFromInput(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
            error={Boolean(effectiveFromError)}
            helperText={
              effectiveFromError ?? 'When institution or address changes, start the new affiliation on this date.'
            }
          />
        </>
      ) : null}

      <Box>
        <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Save profile'}
        </Button>
      </Box>
    </Box>
  );
}
